import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StrengthData } from '../../MoleculeSearch.type';
import StrengthCard from './components/StrengthCard/StrengthCard';
import { StrengthCardProps } from './components/StrengthCard/StrengthCard';
import './AvailableStrengthsProductVariantsSection.styles.scss';
import pillsIcon from 'assets/icons/pillsIcon.svg';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';

interface AvailableStrengthsProductVariantsSectionProps {
  strengths: StrengthData[];
  loading?: boolean;
  error?: string | null;
}

/**
 * Skeleton loader for strength cards using PrimeReact Skeleton component
 */
const StrengthCardSkeleton: React.FC = () => (
  <Skeleton width="100%" height="30rem" borderRadius="var(--border-radius)" />
);

const AvailableStrengthsProductVariantsSection: React.FC<
  AvailableStrengthsProductVariantsSectionProps
> = ({ strengths, loading = false, error = null }) => {
  const [searchParams] = useSearchParams();
  // Get molecule search parameters from URL
  const item = searchParams.get('item');
  const type = searchParams.get('type');
  const label = searchParams.get('label');
  const [viewType, setViewType] = useState<'cards' | 'table'>('cards');
  const navigate = useNavigate();
  const handleViewToggle = (view: 'cards' | 'table') => {
    setViewType(view);
  };

  const handleAnalyzeClick = (strengthData?: StrengthData) => {
    if (strengthData && strengthData.gfc && strengthData.gfc.trim() !== '') {
      // Build URL with all necessary parameters for breadcrumb
      const params = new URLSearchParams();
      params.set('gfc', strengthData.gfc.trim());

      // Add molecule search parameters if available
      if (item) {
        params.set('item', item);
      }
      if (type) {
        params.set('type', type);
      }
      if (label) {
        params.set('label', label);
      }

      // Add strength and dosage form for breadcrumb
      if (strengthData.strength) {
        params.set('strength', strengthData.strength);
      }
      if (strengthData.dosageForms) {
        params.set('dosageForms', strengthData.dosageForms);
      }

      navigate(`/bdportal/product-wiki/product-analysis?${params.toString()}`);
    } else {
      navigate(`/bdportal/product-wiki/product-analysis`);
    }
  };

  return (
    <section id="available-strengths" className="content-section">
      <div className="section-header">
        <div className="section-title">
          <img src={pillsIcon} alt="Pills" />
          Available Strengths & Product Variants
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewType === 'cards' ? 'active' : ''}`}
            onClick={() => handleViewToggle('cards')}
          >
            {/* <img src={cardsIcon} alt="Cards" />  */}
            Cards
          </button>
          <button
            className={`toggle-btn ${viewType === 'table' ? 'active' : ''}`}
            onClick={() => handleViewToggle('table')}
          >
            <i className="fas fa-table"></i> Table
          </button>
        </div>
      </div>
      <div className="section-content">
        {loading ? (
          <div className="three-card-grid">
            {[...Array(6)].map((_, index) => (
              <div key={`skeleton-${index}`} className="three-card-wrapper">
                <StrengthCardSkeleton />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="strengths-error" style={{ padding: '2rem', textAlign: 'center' }}>
            <i
              className="fas fa-exclamation-triangle"
              style={{ color: 'var(--error-color)', marginRight: '0.5rem' }}
            />
            <span>{error}</span>
          </div>
        ) : viewType === 'cards' ? (
          <div className="three-card-grid">
            {strengths && strengths.length > 0 ? (
              strengths.map((strength, index) => (
                <div key={index} className="three-card-wrapper">
                  <StrengthCard
                    {...(strength as StrengthCardProps)}
                    moleculeItem={item || ''}
                    moleculeType={type || ''}
                    moleculeLabel={label || ''}
                  />
                </div>
              ))
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                No strength data available
              </div>
            )}
          </div>
        ) : (
          <div className="table-view">
            <table className="overview-table">
              <thead>
                <tr>
                  <th>
                    <strong>Strength</strong>
                  </th>
                  <th>
                    <strong>Dosage Form</strong>
                  </th>
                  <th>
                    <strong>Route</strong>
                  </th>
                  <th>
                    <strong>Market Leader</strong>
                  </th>
                  <th>
                    <strong>Market Size</strong>
                  </th>
                  <th>
                    <strong>Total NDCs</strong>
                  </th>
                  <th>
                    <strong>Active NDCs</strong>
                  </th>
                  {/* <th>
                    <strong>Opportunity</strong>
                  </th> */}
                  <th>
                    <strong>Action</strong>
                  </th>
                </tr>
              </thead>
              <tbody>
                {strengths.map((strength, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{strength.strength}</strong>
                    </td>
                    <td>{strength.dosageForms.split(',')[0]}</td>
                    <td>{strength.route}</td>
                    <td>{strength.manufacturers[0]}</td>
                    <td>{strength.marketSize}</td>
                    <td>{strength.allNdcs ?? 0}</td>
                    <td>{strength.ndcs}</td>
                    {/* <td>
                      <span
                        style={{
                          color: strength.marketOpportunity.includes('High')
                            ? 'var(--success-color)'
                            : strength.marketOpportunity.includes('Medium')
                              ? 'var(--warning-color)'
                              : 'var(--error-color)',
                          fontWeight: 'bold',
                        }}
                      >
                        {strength.marketOpportunity.includes('High')
                          ? 'High'
                          : strength.marketOpportunity.includes('Medium')
                            ? 'Medium'
                            : 'Low'}
                      </span>
                    </td> */}
                    <td>
                      <button className="action-btn" onClick={() => handleAnalyzeClick(strength)}>
                        View Analysis
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default AvailableStrengthsProductVariantsSection;
